import { useEffect } from "react";
import { useLingui } from "@lingui/react/macro";
import { useLocalStorage } from "usehooks-ts";
import { toast } from "@app/ui";
import { checkNotificationPermission } from "@/lib/Permissions";
import { useRegisterPushSubscription } from "@/queries/push/Query";

// 로그인 후 알림 권한을 "덜 방해되게" 다루는 훅. 예전에는 App.tsx 가 로그인 직후
// registerPushSubscription() 을 무조건 호출해 브라우저/OS 권한 팝업을 콜드로 띄웠다.
// 대신 여기서는 pre-permission priming 을 한다:
//   - granted   → 팝업 없이 조용히 구독만 갱신
//   - prompt    → "알림 켜기" 액션이 달린 소프트 토스트를 딱 1회 노출(실제 프롬프트는
//                 유저가 버튼을 눌러야 뜬다). 노출 여부는 localStorage 로 영구 기억.
//   - denied/unsupported → 아무것도 안 함(설정 화면의 권한 섹션에서만 안내)

const NUDGE_SHOWN_KEY = "notification-nudge-shown";

export function useNotificationNudge(active: boolean): void {
  const { t } = useLingui();
  const registerPushSubscription = useRegisterPushSubscription();
  const [nudgeShown, setNudgeShown] = useLocalStorage(NUDGE_SHOWN_KEY, false);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;

    void (async () => {
      const status = await checkNotificationPermission();
      if (cancelled) return;

      if (status === "granted") {
        // 이미 허용됨 — 팝업 없이 이 기기 구독만 서버에 갱신한다.
        void registerPushSubscription();
        return;
      }

      // prompt(아직 안 물어봄)일 때만, 그리고 아직 안내한 적 없을 때만 1회 노출한다.
      if (status === "prompt" && !nudgeShown) {
        setNudgeShown(true);
        const toastId = toast({
          title: t`알림을 켜면 리마인더를 놓치지 않아요`,
          description: t`할 일 알림을 받으려면 알림을 허용해 주세요.`,
          duration: Infinity,
          action: {
            // 클릭 시에만 실제 권한 프롬프트가 뜨고 구독까지 등록한다.
            // 버튼을 눌렀으면 안내 역할은 끝났으니 토스트도 함께 닫는다
            // (duration: Infinity 라 명시적으로 닫지 않으면 계속 떠 있다).
            label: t`알림 켜기`,
            onClick: () => {
              toast.dismiss(toastId);
              void registerPushSubscription();
            },
          },
        });
      }
      // denied / unsupported 는 여기서 처리하지 않는다(재요청 불가 — 설정에서 안내).
    })();

    return () => {
      cancelled = true;
    };
  }, [active, nudgeShown, setNudgeShown, registerPushSubscription, t]);
}
